import { Action } from 'redux';
import { EMPTY, from, merge, of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { AgreementPriceDto, AgreementStatus } from '../../../common/PricePrgClient';
import { createSaveTableSettingsEpic } from '../../../common/datagrid';
import {
    notifyAgreementDeleted,
    notifyAgreementPriceDeleted,
    notifyAgreementPriceSaved,
    notifyAgreementSaved
} from '../../../common/signalr/signalrActions';
import { RootEpic } from '../../../store';
import {
    createEpicFromAsyncAction,
    createEpicFromAsyncActionEmptyRequest,
    toDateString
} from '../../../utils';
import { fetchStocksForDay } from '../../stock/redux/stockSlices';
import { enqueueSnackbar, pricePrgError } from '../../ui/redux/uiSlices';
import {
    agreementPriceTableActions,
    agreementSelected,
    agreementStockDateSelected,
    agreementTableActions,
    agreementWarehouseSelected,
    copyAgreement,
    copyAgreementRequiresConfirmation,
    createAgreement,
    deleteAgreement,
    fetchAgreementPrices,
    fetchAgreements,
    fetchExternalPrices,
    fetchPrices,
    fetchReservedPrice,
    pricelistDateSelected,
    refExtPricelistIdSelected,
    refPricelistIdSelected,
    saveAgreementPrice,
    saveAgreementPriceDiscountRequest,
    showAllArticlesChanged,
    updateAgreement,
    updateAgreementPriceActiveStatus,
    updateAgreementTags
} from './agreementSlices';

const fetchAgreements$ = createEpicFromAsyncActionEmptyRequest(fetchAgreements, ({ agreementClient }) =>
    agreementClient.getAll()
);

const updateAgreement$: RootEpic = (action$, state$, { agreementClient }) =>
    action$.pipe(
        filter(updateAgreement.request.match),
        withLatestFrom(state$.pipe(map(state => state.user.currentUserPermissions))),
        switchMap(([action, cup]) =>
            from(agreementClient.update(action.payload.agreementId, action.payload)).pipe(
                mergeMap(agreement => {
                    const augIds = agreement.permittedUserGroups.map(x => x.userGroupId);

                    return [
                        // If the user removed all of its user groups from the agreement, it is no longer
                        // permitted to view the agreement. Maybe not the best approach, maybe this
                        // shouldn't be possible for the user to do, or at least show some kind of warning?
                        augIds.length === 0 ||
                        cup.isAdmin ||
                        cup.userGroups.some(ug => augIds.includes(ug.userGroupId))
                            ? updateAgreement.success(agreement)
                            : deleteAgreement.success(agreement.agreementId),
                        notifyAgreementSaved(action.payload.agreementId)
                    ];
                }),
                catchError(err => of(pricePrgError(err)))
            )
        )
    );

const createAgreement$ = createEpicFromAsyncAction(
    createAgreement,
    (payload, { agreementClient }) => agreementClient.create(payload),
    (req, resp) => [agreementSelected(resp.agreementId), notifyAgreementSaved(resp.agreementId)]
);

const deleteAgreement$ = createEpicFromAsyncAction(
    deleteAgreement,
    (payload, { agreementClient }) => agreementClient.delete(payload).then(() => payload),
    req => notifyAgreementDeleted(req)
);

const saveAgreementPriceTable$ = createSaveTableSettingsEpic(
    agreementPriceTableActions,
    state => state.agreement.agreementPriceTable
);
const fetchAgreementPrices$ = createEpicFromAsyncAction(
    fetchAgreementPrices,
    (payload, { agreementPriceClient }) => agreementPriceClient.getByAgreementId(payload.agreementId)
);

const agreementSelected$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(agreementSelected.match),
        withLatestFrom(state$.pipe(map(state => state.agreement.agreements))),
        mergeMap(([action, agreements]) => {
            const actions: Action[] = [fetchAgreementPrices.request({ agreementId: action.payload })];

            const agreement = agreements[action.payload];
            if (agreement != null) {
                actions.push(
                    showAllArticlesChanged(
                        agreement.agreementStatus == null || agreement.agreementStatus === AgreementStatus.New
                    )
                );
            }

            return actions;
        })
    );

const fetchRefPrices$ = createEpicFromAsyncAction(fetchPrices, (payload, { priceClient }) =>
    priceClient.getPrices([payload.pricelistId], toDateString(payload.pricelistDate))
);
const fetchRefExtPrices$ = createEpicFromAsyncAction(
    fetchExternalPrices,
    (payload, { externalPriceClient }) =>
        externalPriceClient.getPricesByPricelistAndDate(toDateString(payload.pricelistDate), [
            payload.pricelistId
        ])
);

const refPricelistSelected$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(refPricelistIdSelected.match),
        withLatestFrom(state$),
        map(([action, state]) =>
            fetchPrices.request({
                pricelistDate: state.agreement.ui.pricelistDate,
                pricelistId: action.payload
            })
        )
    );

const refExtPricelistSelected$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(refExtPricelistIdSelected.match),
        withLatestFrom(state$),
        map(([action, state]) =>
            fetchExternalPrices.request({
                pricelistDate: state.agreement.ui.pricelistDate,
                pricelistId: action.payload
            })
        )
    );

const pricelistDateSelected$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(pricelistDateSelected.match),
        withLatestFrom(state$),
        mergeMap(([action, state]) => {
            const actions: Action[] = [];
            if (state.agreement.ui.refExtPricelistId != null) {
                actions.push(
                    fetchExternalPrices.request({
                        pricelistId: state.agreement.ui.refExtPricelistId,
                        pricelistDate: action.payload
                    })
                );
            }
            if (state.agreement.ui.refPricelistId != null) {
                actions.push(
                    fetchPrices.request({
                        pricelistId: state.agreement.ui.refPricelistId,
                        pricelistDate: action.payload
                    })
                );
            }
            return actions;
        })
    );

const fetchWarehouseBalance$: RootEpic = (action$, state$) =>
    merge(
        action$.pipe(
            filter(agreementWarehouseSelected.match),
            withLatestFrom(state$.pipe(map(state => state.agreement.ui.stockDate))),
            map(([action, selectedDate]) =>
                fetchStocksForDay.request({
                    date: selectedDate,
                    warehouseId: action.payload
                })
            )
        ),
        action$.pipe(
            filter(agreementStockDateSelected.match),
            withLatestFrom(state$.pipe(map(state => state.agreement.ui.warehouseId))),
            filter(([, warehouse]) => warehouse != null),
            map(([action, warehouse]) =>
                fetchStocksForDay.request({
                    date: action.payload,
                    warehouseId: warehouse!
                })
            )
        )
    );

const saveAgreementPrice$ = createEpicFromAsyncAction(
    saveAgreementPrice,
    (payload, { agreementPriceClient }) =>
        agreementPriceClient
            .savePrice(payload.agreementId, payload)
            .then(resp => ({ type: payload.type, ...resp })),
    (_, resp) =>
        resp.price > 0 ? notifyAgreementPriceSaved(resp.agreementPriceId) : notifyAgreementPriceDeleted(resp),
    (req, err) => [saveAgreementPrice.failure(req), pricePrgError(err)]
);

const saveAgreementPriceDiscount$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(saveAgreementPriceDiscountRequest.match),
        withLatestFrom(
            state$.pipe(map(state => state.agreement.agreementPrices)),
            state$.pipe(map(state => state.agreement.pricesToBeSaved))
        ),
        mergeMap(([action, agreementPrices, priceToBeSaved]) => {
            const price = agreementPrices[action.payload.agreementPriceId];
            if (price == null) {
                return EMPTY;
            }

            return of(
                saveAgreementPrice.request({
                    ...price,
                    price: priceToBeSaved[price.productId]?.price ?? price.price,
                    externalPrice: priceToBeSaved[price.productId]?.extPrice ?? price.externalPrice,
                    discount:
                        action.payload.type === 'in' ? action.payload.discount ?? undefined : price.discount,
                    externalDiscount:
                        action.payload.type === 'ext'
                            ? action.payload.discount ?? undefined
                            : price.externalDiscount,
                    type: 'discount'
                })
            );
        })
    );

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function hasPriceConflicts(copyResponse: any): copyResponse is { priceConflicts: AgreementPriceDto[] } {
    return copyResponse.priceConflicts != null && copyResponse.priceConflicts.length > 0;
}

const copyAgreement$: RootEpic = (action$, state$, { agreementClient }) =>
    action$.pipe(
        filter(copyAgreement.request.match),
        mergeMap(action =>
            from(
                agreementClient.copy(
                    action.payload.agreementId,
                    action.payload.awareOfPriceConflicts ?? false,
                    action.payload.additionalDetails
                )
            ).pipe(
                mergeMap(resp => {
                    if (hasPriceConflicts(resp)) {
                        return of(copyAgreementRequiresConfirmation(resp.priceConflicts));
                    }

                    return [
                        copyAgreement.success(resp),
                        notifyAgreementSaved(resp.agreementId),
                        agreementSelected(resp.agreementId)
                    ];
                }),
                catchError(err => of(pricePrgError(err)))
            )
        )
    );

const fetchReservedPrice$ = createEpicFromAsyncAction(
    fetchReservedPrice,
    (payload, { agreementPriceClient }) =>
        agreementPriceClient.getReservedPrice(payload.agreementId, payload.productId)
);

const agreementTable$ = createSaveTableSettingsEpic(
    agreementTableActions,
    state => state.agreement.agreementTable
);

const updateAgreementPriceActiveStatus$: RootEpic = (action$, state$) =>
    action$.pipe(
        filter(updateAgreementPriceActiveStatus.match),
        withLatestFrom(state$.pipe(map(state => state.agreement.agreementPrices))),
        map(
            ([action, agreementPrices]) =>
                [action.payload.active, agreementPrices[action.payload.agreementPriceId]] as const
        ),
        filter(([, ap]) => ap != null),
        map(([isActive, ap]) =>
            saveAgreementPrice.request({
                ...ap,
                isActive,
                type: 'isActive'
            })
        )
    );

const updateAgreementTags$: RootEpic = (action$, state$, { tagClient }) =>
    action$.pipe(
        filter(updateAgreementTags.request.match),
        withLatestFrom(state$.pipe(map(state => state.tags.tags))),
        mergeMap(([action, tags]) =>
            from(tagClient.addTag(action.payload.agreementId!, action.payload)).pipe(
                mergeMap(() => [
                    updateAgreementTags.success({
                        agreementId: action.payload.agreementId!,
                        tags: action.payload.tags.map(t => tags[t])
                    }),
                    enqueueSnackbar({
                        message: 'Saved agreement tags',
                        severity: 'success',
                        autoHideDuration: 3000
                    })
                ]),
                catchError(err => of(pricePrgError(err)))
            )
        )
    );

// = createEpicFromAsyncAction(
//     updateAgreementTags,
//     (req, { tagClient }) => tagClient.addTag(req.agreementId!, req).then(),
//     req => getAgreementTags.request(req.agreementId!)
// );
export default [
    fetchAgreements$,
    updateAgreement$,
    createAgreement$,
    fetchAgreementPrices$,
    agreementSelected$,
    fetchRefPrices$,
    refPricelistSelected$,
    refExtPricelistSelected$,
    fetchRefExtPrices$,
    pricelistDateSelected$,
    fetchWarehouseBalance$,
    saveAgreementPrice$,
    saveAgreementPriceDiscount$,
    copyAgreement$,
    deleteAgreement$,
    fetchReservedPrice$,
    saveAgreementPriceTable$,
    agreementTable$,
    updateAgreementPriceActiveStatus$,
    updateAgreementTags$
];
